import React, { useState, useEffect } from "react";
import ImportPresetWinnersDialogComponent from "./importPresetWinnersDialog.component";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Papa from "papaparse";
import { getApiLang } from "app/feature/constants";
import { addAlert, setLoading } from "modules/notification";
import { hideBackdrop, showBackdrop } from "modules/backdrop";
import { SET_WINNER_LUCKY_DRAW_LOADING } from "modules/lucky-draw-v2/constants";
import luckyDrawV2Api from "app/api/luckyDrawV2";

function ImportPresetWinnersDialogContainer({ 
  isOpen, 
  handleClose, 
  luckyDrawUuid,
  prizeId,
  onImportSuccess
}) {

  const dispatch = useDispatch();
  const lang = useSelector((state) => state.constant.languages);
  
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [showTemplateInput, setShowTemplateInput] = useState(false);

  const [tableHeader, setTableHeader] = useState([]);
  const [tableValues, setTableValues] = useState([]);
  const [errorField, setErrorField] = useState(null);
  const [errorTitles, setErrorTitles] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const [isValidFile, setIsValidFile] = useState(false);
  const [filePath, setFilePath] = useState(null);
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  
  // Add state for invalid serial numbers visualization
  const [invalidSerialNumbers, setInvalidSerialNumbers] = useState([]);
  const [validSerialNumbers, setValidSerialNumbers] = useState([]);
  const [allSerialsStatus, setAllSerialsStatus] = useState(null);
  const [showInvalidSerialsTable, setShowInvalidSerialsTable] = useState(false);

  // Add useEffect to check dialog states
  useEffect(() => {
  }, [isOpen, showTemplateInput, lang]);

  const handleValidate = async () => {
    setIsValidating(true);
    
    try {
      // Call the API to validate the file
      const response = await luckyDrawV2Api.validateWinnerImport({
        file,
        luckyDrawUuid,
        prizeId
      });

      
      if (response.success) {
        if (response.data?.file_path) {
          setFilePath(response.data.file_path);
        }
        setIsValidFile(true);
        dispatch(
          addAlert({
            severity: "success",
            message: getApiLang(lang, "success.SUCCESS_FILE_VALIDATED_SUCCESSFULLY"),
          })
        );
      } else {
        handleValidationError(response);
      }
    } catch (error) {
      // Check if error message is a JSON string
      let parsedError = error;
      try {
        if (typeof error.message === 'string') {
          const jsonError = JSON.parse(error.message);
          if (jsonError && jsonError.code) {
            parsedError = jsonError;
          }
        }
      } catch (parseError) {
      }
      
      // Check if the error object itself has the structure we need
      if (!parsedError.code && error.code) {
        parsedError = error;
      }
      
      // Get error code from parsed object or original error
      const errorCode = parsedError.code || error.code || "error.ERROR_SOMETHING_WRONG";
        
      let errorMessage = getApiLang(lang, errorCode);
      
      // Handle invalid SN visualization
      if (errorCode === "error.ERROR_INVALID_SERIAL_NUMBERS_LUCKYDRAW_IMPORT") {
        
        let invalidSerials = [];
        let validSerials = [];
        let statusData = null;
        
        // different possible DS
        if (parsedError.data && parsedError.data.invalid_serials) {
          invalidSerials = parsedError.data.invalid_serials;
          validSerials = parsedError.data.valid_serials || [];
          statusData = parsedError.data.all_serials_status || null;
        } else if (parsedError.invalid_serials) {
          invalidSerials = parsedError.invalid_serials;
          validSerials = parsedError.valid_serials || [];
        } else if (Array.isArray(parsedError.data)) {
          invalidSerials = parsedError.data;
        }
        
        
        if ((Array.isArray(invalidSerials) && invalidSerials.length > 0) || 
            (Array.isArray(validSerials) && validSerials.length > 0)) {
          setInvalidSerialNumbers(invalidSerials);
          setValidSerialNumbers(validSerials);
          setAllSerialsStatus(statusData);
          setShowInvalidSerialsTable(true);
        }
        
        if (parsedError.data && parsedError.data.message) {
          errorMessage = `${errorMessage}: ${parsedError.data.message}`;
        }
      } else if (parsedError.message && parsedError.message !== errorCode && typeof parsedError.message === 'string') {
        errorMessage = parsedError.message;
      } else if (parsedError.data && parsedError.data.message) {
        errorMessage += `: ${parsedError.data.message}`;
      }
      
      dispatch(
        addAlert({
          severity: "error",
          message: errorMessage
        })
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidationError = (response) => {
    if (!response.success) {
      let errorCode = response.code;
      
      if (response.code === "error.ERROR_INVALID_COLUMN_COUNT") {
        setShowPreview(true);
        setErrorField({
          row: response.data.row,
          column: response.data.column,
        });
      } else if (response.code === "error.ERROR_INVALID_ADDITIONAL_INFO_TITLE") {
        setShowPreview(true);
        let columns = response.data.map((d) => d.column);
        setErrorTitles(errorTitles.concat(columns));
      } else if (
        response.code === "error.ERROR_MISSING_COLUMN_FIELD_LUCKYDRAW" ||
        response.code === "error.ERROR_INVALID_DATE_FORMAT" ||
        response.code === "error.ERROR_INVALID_SERIAL_NUMBER"
      ) {
        setShowPreview(true);
        setErrorField({
          row: response.data.row,
          column: response.data.column,
        });
      } else if (response.code === "error.ERROR_INVALID_SERIAL_NUMBERS_LUCKYDRAW_IMPORT" && response.data) {
        // Handle invalid SN visualization
        const invalidSerials = response.data.invalid_serials || [];
        const validSerials = response.data.valid_serials || [];
        const statusData = response.data.all_serials_status || null;
        
        if ((Array.isArray(invalidSerials) && invalidSerials.length > 0) || 
            (Array.isArray(validSerials) && validSerials.length > 0))
          setInvalidSerialNumbers(invalidSerials);
          setValidSerialNumbers(validSerials);
          setAllSerialsStatus(statusData);
          setShowInvalidSerialsTable(true);
      }
      
      let errorMessage = getApiLang(lang, errorCode);
      
      if (response.message && typeof response.message === 'string') {
        errorMessage = response.message;
      } else if (response.data && response.data.message) {
        errorMessage += `: ${response.data.message}`;
      }
      
      dispatch(
        addAlert({
          severity: "error",
          message: errorMessage,
        })
      );
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    dispatch(setLoading({ id: SET_WINNER_LUCKY_DRAW_LOADING, state: true }));
    dispatch(showBackdrop());
    
    try {
      await luckyDrawV2Api.importWinners({
        file_path: filePath,
        luckyDrawUuid,
        prizeId
      });
      
      dispatch(
        addAlert({
          severity: "success",
          message: getApiLang(lang, "success.SUCCESS_IMPORT_PRESET_WINNERS"),
        })
      );
      
      handleClosePopUp();
      
      // Notify parent component that import was successful
      if (onImportSuccess) {
        // Add a small delay to ensure backend has completed processing
        setTimeout(() => {
          onImportSuccess();
        }, 100);
      }
    } catch (error) {  
      // Check if error message is a JSON string
      let parsedError = error;
        if (typeof error.message === 'string') {
          const jsonError = JSON.parse(error.message);
          if (jsonError && jsonError.code) {
            parsedError = jsonError;
          }
        }
      
      // Check if the error object itself has the structure we need
      if (!parsedError.code && error.code) {
        parsedError = error;
      }
      
      // Get error code from parsed object or original error
      const errorCode = parsedError.code || error.code || "error.ERROR_SOMETHING_WRONG";
      
      let errorMessage = getApiLang(lang, errorCode);
      
      // Handle invalid SN visualization
      if (errorCode === "error.ERROR_INVALID_SERIAL_NUMBERS_LUCKYDRAW_IMPORT") {
      
        let invalidSerials = [];
        let validSerials = [];
        let statusData = null;
        
        // Different possible DS
        if (parsedError.data && parsedError.data.invalid_serials) {
          invalidSerials = parsedError.data.invalid_serials;
          validSerials = parsedError.data.valid_serials || [];
          statusData = parsedError.data.all_serials_status || null;
        } else if (parsedError.invalid_serials) {
          invalidSerials = parsedError.invalid_serials;
          validSerials = parsedError.valid_serials || [];
        } else if (Array.isArray(parsedError.data)) {
          invalidSerials = parsedError.data;
        }
        
        if ((Array.isArray(invalidSerials) && invalidSerials.length > 0) || 
            (Array.isArray(validSerials) && validSerials.length > 0)) {
          setInvalidSerialNumbers(invalidSerials);
          setValidSerialNumbers(validSerials);
          setAllSerialsStatus(statusData);
          setShowInvalidSerialsTable(true);
        }
        
        if (parsedError.data && parsedError.data.message) {
          errorMessage = `${errorMessage}: ${parsedError.data.message}`;
        }
      } else if (parsedError.message && parsedError.message !== errorCode && typeof parsedError.message === 'string') {
        errorMessage = parsedError.message;
      } else if (parsedError.data && parsedError.data.message) {
        errorMessage += `: ${parsedError.data.message}`;
      }
      
      dispatch(
        addAlert({
          severity: "error",
          message: errorMessage
        })
      );
    } finally {
      dispatch(hideBackdrop());
      dispatch(setLoading({ id: SET_WINNER_LUCKY_DRAW_LOADING, state: false }));
      setIsSubmitting(false);
    }
  };

  const handleClosePopUp = () => {
    handleClose();
    setShowTemplateInput(false);
    setFile(null);
    setIsValidFile(false);
    setErrorField(null);
    setErrorTitles([]);
    setShowPreview(false);
    setInvalidSerialNumbers([]);
    setValidSerialNumbers([]);
    setAllSerialsStatus(null);
    setShowInvalidSerialsTable(false);
  };

  const showAlert = (value) =>
    dispatch(addAlert({ severity: "warning", message: value }));

  const handleFileChange = async (file) => {
    if (file) {
      setShowPreview(false);
      setErrorTitles([]);
      setErrorField(null);
      setIsValidFile(false);
      setInvalidSerialNumbers([]);
      setValidSerialNumbers([]);
      setAllSerialsStatus(null);
      setShowInvalidSerialsTable(false);
      setIsFileProcessing(true);
      
      try {
        Papa.parse(file, {
          header: true,
          worker: true,
          skipEmptyLines: true,
          complete: function (results) {
            const rowsArray = [];
            const valuesArray = [];

            // Iterating data to get column name and their values
            results.data.forEach((d) => {
              rowsArray.push(Object.keys(d));
              valuesArray.push(Object.values(d));
            });

            // Filtered Column Names
            setTableHeader(rowsArray[0]);

            // Filtered Values
            setTableValues(valuesArray);
            setIsFileProcessing(false);
          },
        });
      } catch (error) {
        setIsFileProcessing(false);
      }
    }
  };

  return (
    <ImportPresetWinnersDialogComponent
      showTemplateInput={showTemplateInput}
      setShowTemplateInput={setShowTemplateInput}
      handleValidate={handleValidate}
      isOpen={isOpen}
      handleClose={handleClosePopUp}
      lang={lang}
      addAlert={showAlert}
      isSubmitting={isSubmitting}
      setFile={setFile}
      file={file}
      handleFileChange={handleFileChange}
      tableHeader={tableHeader}
      tableValues={tableValues}
      errorField={errorField}
      errorTitles={errorTitles}
      showPreview={showPreview}
      isValidFile={isValidFile}
      handleSubmit={handleSubmit}
      isValidating={isValidating}
      isFileProcessing={isFileProcessing}
      luckyDrawUuid={luckyDrawUuid}
      prizeId={prizeId}
      invalidSerialNumbers={invalidSerialNumbers}
      validSerialNumbers={validSerialNumbers}
      allSerialsStatus={allSerialsStatus}
      showInvalidSerialsTable={showInvalidSerialsTable}
    />
  );
}

ImportPresetWinnersDialogContainer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  luckyDrawUuid: PropTypes.string.isRequired,
  prizeId: PropTypes.number.isRequired,
  onImportSuccess: PropTypes.func
};

export default ImportPresetWinnersDialogContainer;

